import { ConfigProvider, Segmented } from "antd";
import type { SegmentedProps } from "antd";
import { cn } from "@/shared/lib/utils";

// antd marks an option "selected" (white text) only once the sliding thumb has
// arrived, so the text turned white late. Its radio input is checked at once:
// colour the text from that and fade it over the thumb's own slide (antd's
// motionDurationSlow + motionEaseInOut), exactly like PillTabs.
const TEXT_IN_STEP_WITH_THUMB = cn(
  "[&_.ant-segmented-item]:![transition:color_0.3s_cubic-bezier(0.645,0.045,0.355,1)]",
  "[&_.ant-segmented-item:has(>input:checked)]:!text-white",
);

type ThemedSegmentedProps<T> = SegmentedProps<T> & {
  /** Background of the sliding selected pill. */
  selectedBg: string;
  /** Text color of unselected options. */
  itemColor: string;
  /** Text color of unselected options on hover (defaults to selectedBg). */
  itemHoverColor?: string;
  /** Inner padding between the track edge and the pill. */
  trackPadding?: number;
};

/**
 * antd Segmented with its colors set through component tokens. Never color
 * the selected option with class overrides (e.g. `.ant-segmented-item-selected`
 * `!bg-…`): that paints the new option instantly and hides antd's sliding thumb,
 * so the switch snaps instead of sliding.
 */
export function ThemedSegmented<T>({
  selectedBg, itemColor, itemHoverColor, trackPadding = 4, className, ...props
}: ThemedSegmentedProps<T>) {
  return (
    <ConfigProvider
      theme={{
        components: {
          Segmented: {
            trackBg: "#FFFFFF",
            trackPadding,
            itemSelectedBg: selectedBg,
            itemSelectedColor: "#FFFFFF",
            itemColor,
            itemHoverColor: itemHoverColor ?? selectedBg,
            itemHoverBg: "transparent",
            itemActiveBg: "transparent",
          },
        },
      }}
    >
      <Segmented<T> {...props} className={cn(TEXT_IN_STEP_WITH_THUMB, className)} />
    </ConfigProvider>
  );
}
